import { useEffect, useState } from 'react';
import { getSupabase } from '../../components/supabaseClient';
import { fetchEffectiveUserRow } from '../../utils/effectiveUserRole';

// Compute initials from a name: "Omar Shahwan" → "OS", "Omar" → "O".
// Falls back to email local-part's first letter, then "?".
function computeInitials({ name, email }) {
  if (name && typeof name === 'string') {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts.length === 1 && parts[0].length > 0) return parts[0][0].toUpperCase();
  }
  if (email && typeof email === 'string' && email.length > 0) return email[0].toUpperCase();
  return '?';
}

// Loads the active user's id/name/email/initials. Mirrors Navbar's pattern:
//   supabase.auth.getSession() → session.user.id
//   supabase.from('users').select('name').eq('id', user.id).single()
// Returns { userId, name, email, initials, role, patientUserRow, loading }.
export function useUserInfo() {
  const [state, setState] = useState({
    userId: null,
    name: null,
    email: null,
    initials: '?',
    role: null,
    patientUserRow: null,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sb = await getSupabase();
        const { data: { session } } = await sb.auth.getSession();
        const user = session?.user;
        if (!user) {
          if (!cancelled) setState({ userId: null, name: null, email: null, initials: '?', role: null, patientUserRow: null, loading: false });
          return;
        }
        const email = user.email || null;
        // Try the `users` table for a display name; missing row is fine.
        let name = null;
        let userRow = null;
        try {
          const { data } = await fetchEffectiveUserRow({ id: user.id });
          userRow = data || null;
          name = data?.name || null;
        } catch { /* table-not-found or RLS — fall back to email */ }
        const initials = computeInitials({ name, email });
        if (!cancelled) setState({
          userId: user.id,
          name,
          email,
          initials,
          role: userRow?.role || null,
          patientUserRow: userRow,
          loading: false,
        });
      } catch (err) {
        if (!cancelled) setState({ userId: null, name: null, email: null, initials: '?', role: null, patientUserRow: null, loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return state;
}
